import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, FilePdf, Trash, Plus, Coins, Warning, Check, Users, Moon, MapPin } from '@phosphor-icons/react';
import SignaturePad from '../components/SignaturePad';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EMPTY_FORM = {
  receipt_number: '',
  receipt_date: '',
  locatore_nome: '',
  locatore_cf: '',
  ospite_nome: '',
  ospite_cf: '',
  indirizzo: '',
  comune: '',
  dal: '',
  al: '',
  num_ospiti: 1,
  num_ospiti_esenti: 0,
  num_notti: 1,
  tariffa_notte: '',
  luogo_data: '',
  note: '',
};

export default function TouristTaxReceiptsPage() {
  const navigate = useNavigate();
  const [view, setView] = useState('list'); // list | create
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedSignature, setSavedSignature] = useState(null);
  const [signatureFromSaved, setSignatureFromSaved] = useState(false);
  const sigPadRef = useRef(null);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    fetchReceipts();
    fetchOwnerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReceipts = async () => {
    try {
      const res = await axios.get(`${API}/tourist-tax-receipts`, { withCredentials: true });
      setReceipts(res.data);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  const fetchOwnerData = async () => {
    try {
      const res = await axios.get(`${API}/owner-data`, { withCredentials: true });
      if (res.data) {
        setForm((f) => ({
          ...f,
          locatore_nome: res.data.full_name || '',
          locatore_cf: res.data.tax_code || '',
        }));
      }
    } catch { /* not set yet */ }
    try {
      const r = await axios.get(`${API}/owner/profile`, { withCredentials: true });
      if (r.data?.signature) setSavedSignature(r.data.signature);
    } catch { /* no signature */ }
  };

  // Auto-compute nights from dal/al
  useEffect(() => {
    if (form.dal && form.al) {
      const d1 = new Date(form.dal);
      const d2 = new Date(form.al);
      if (!Number.isNaN(d1) && !Number.isNaN(d2) && d2 > d1) {
        const nights = Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
        setForm((f) => ({ ...f, num_notti: nights }));
      }
    }
  }, [form.dal, form.al]);

  const parseAmount = (v) => parseFloat(String(v).replace(',', '.') || '0') || 0;
  const paying = Math.max(0, (parseInt(form.num_ospiti) || 0) - (parseInt(form.num_ospiti_esenti) || 0));
  const totale = paying * (parseInt(form.num_notti) || 0) * parseAmount(form.tariffa_notte);

  const useSavedSignature = () => {
    if (!savedSignature) return;
    setSignatureFromSaved(true);
    toast.success('Firma salvata applicata');
  };

  const clearSignature = () => {
    sigPadRef.current?.clear();
    setSignatureFromSaved(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.ospite_nome || !form.indirizzo || !form.dal || !form.al) {
      toast.error('Compila almeno ospite, indirizzo e periodo');
      return;
    }
    let signature = null;
    if (signatureFromSaved) signature = savedSignature;
    else if (sigPadRef.current && !sigPadRef.current.isEmpty()) {
      signature = sigPadRef.current.toDataURL();
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        tariffa_notte: String(form.tariffa_notte || '0').replace('.', ','),
        totale_imposta: totale.toFixed(2).replace('.', ','),
        firma_locatore: signature,
      };
      const res = await axios.post(`${API}/tourist-tax-receipts`, payload, { withCredentials: true });
      toast.success('Ricevuta creata!');
      // Auto-download
      try {
        const pdfRes = await axios.get(`${API}/tourist-tax-receipts/${res.data.id}/pdf`, { withCredentials: true, responseType: 'blob' });
        const url = URL.createObjectURL(pdfRes.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ricevuta_imposta_${form.receipt_number || res.data.id}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      } catch { /* silent */ }
      setForm(EMPTY_FORM);
      setSignatureFromSaved(false);
      setView('list');
      fetchReceipts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore salvataggio');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa ricevuta?')) return;
    try {
      await axios.delete(`${API}/tourist-tax-receipts/${id}`, { withCredentials: true });
      toast.success('Ricevuta eliminata');
      fetchReceipts();
    } catch { toast.error('Errore eliminazione'); }
  };

  const downloadPdf = async (r) => {
    try {
      const res = await axios.get(`${API}/tourist-tax-receipts/${r.id}/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `ricevuta_imposta_${r.receipt_number || r.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Errore download PDF'); }
  };

  if (loading) return <div className="p-8 text-center text-zinc-400">Caricamento…</div>;

  return (
    <div className="max-w-5xl mx-auto" data-testid="tourist-tax-receipts-page">
      {/* Header */}
      <div className="warm-card p-5 sm:p-7 mb-6 relative overflow-hidden">
        <div className="absolute -top-16 -right-10 w-56 h-56 bg-gradient-to-br from-teal-100/60 to-transparent rounded-full blur-3xl pointer-events-none" />
        <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/contabilita')} className="p-2 hover:bg-zinc-100 rounded-lg" data-testid="back-btn">
              <ArrowLeft size={20} />
            </button>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-teal-700 mb-1">Contabilità</p>
              <h1 className="font-geist text-2xl sm:text-3xl font-black text-zinc-900 tracking-tight flex items-center gap-2">
                <Coins size={24} weight="fill" className="text-amber-500" />
                Ricevute Imposta di Soggiorno
              </h1>
              <p className="text-sm text-zinc-500 mt-1">Genera e archivia le ricevute rilasciate agli ospiti per l'imposta comunale.</p>
            </div>
          </div>
          <Button
            onClick={() => setView(view === 'list' ? 'create' : 'list')}
            className="bg-zinc-900 hover:bg-zinc-800 text-white font-bold rounded-xl px-5"
            data-testid="toggle-create-btn"
          >
            {view === 'list' ? (<><Plus size={16} className="mr-1.5" /> Nuova Ricevuta</>) : 'Torna alla lista'}
          </Button>
        </div>
      </div>

      {view === 'create' ? (
        <motion.form
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={handleSave}
          className="warm-card p-5 sm:p-7 space-y-6"
          data-testid="tourist-tax-create-form"
        >
          {/* Riepilogo live */}
          <div className="bg-gradient-to-br from-teal-50 to-emerald-50 border border-teal-200 rounded-2xl p-4" data-testid="live-summary">
            <p className="text-xs font-black uppercase tracking-widest text-teal-700 mb-2">Totale imposta calcolato</p>
            <p className="text-4xl font-black text-teal-900 tabular-nums">€ {totale.toFixed(2).replace('.', ',')}</p>
            <p className="text-xs text-teal-700 mt-1">
              {paying} ospiti paganti × {form.num_notti || 0} notti × € {form.tariffa_notte || '0,00'} / notte
            </p>
          </div>

          {/* Numerazione + Data */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">N° ricevuta</label>
              <Input value={form.receipt_number} onChange={(e) => setForm({ ...form, receipt_number: e.target.value })} placeholder="es. 2026-01" data-testid="receipt-number-input" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">Data ricevuta</label>
              <Input type="date" value={form.receipt_date} onChange={(e) => setForm({ ...form, receipt_date: e.target.value })} data-testid="receipt-date-input" />
            </div>
          </div>

          {/* Locatore + Ospite */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <p className="text-xs font-black uppercase tracking-widest text-zinc-500">Locatore</p>
              <Input placeholder="Nome cognome" value={form.locatore_nome} onChange={(e) => setForm({ ...form, locatore_nome: e.target.value })} data-testid="locatore-nome" />
              <Input placeholder="Codice fiscale" value={form.locatore_cf} onChange={(e) => setForm({ ...form, locatore_cf: e.target.value })} data-testid="locatore-cf" />
            </div>
            <div className="space-y-3">
              <p className="text-xs font-black uppercase tracking-widest text-zinc-500">Ospite</p>
              <Input placeholder="Nome cognome" required value={form.ospite_nome} onChange={(e) => setForm({ ...form, ospite_nome: e.target.value })} data-testid="ospite-nome" />
              <Input placeholder="Codice fiscale" value={form.ospite_cf} onChange={(e) => setForm({ ...form, ospite_cf: e.target.value })} data-testid="ospite-cf" />
            </div>
          </div>

          {/* Immobile + Comune */}
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5"><MapPin size={12} /> Immobile</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Input placeholder="Indirizzo immobile" required value={form.indirizzo} onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} data-testid="indirizzo" />
              </div>
              <Input placeholder="Comune" value={form.comune} onChange={(e) => setForm({ ...form, comune: e.target.value })} data-testid="comune" />
            </div>
          </div>

          {/* Periodo */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">Dal</label>
              <Input type="date" required value={form.dal} onChange={(e) => setForm({ ...form, dal: e.target.value })} data-testid="dal" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">Al</label>
              <Input type="date" required value={form.al} onChange={(e) => setForm({ ...form, al: e.target.value })} data-testid="al" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1 flex items-center gap-1"><Moon size={12} weight="fill" /> Notti</label>
              <Input type="number" min={1} value={form.num_notti} onChange={(e) => setForm({ ...form, num_notti: parseInt(e.target.value) || 1 })} data-testid="num-notti" />
            </div>
          </div>

          {/* Ospiti + tariffa */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1 flex items-center gap-1"><Users size={12} weight="fill" /> Totale ospiti</label>
              <Input type="number" min={1} value={form.num_ospiti} onChange={(e) => setForm({ ...form, num_ospiti: parseInt(e.target.value) || 1 })} data-testid="num-ospiti" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">di cui esenti (minori)</label>
              <Input type="number" min={0} value={form.num_ospiti_esenti} onChange={(e) => setForm({ ...form, num_ospiti_esenti: parseInt(e.target.value) || 0 })} data-testid="num-ospiti-esenti" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">Tariffa/notte (€)</label>
              <Input type="text" inputMode="decimal" placeholder="es. 2,50" value={form.tariffa_notte} onChange={(e) => setForm({ ...form, tariffa_notte: e.target.value })} data-testid="tariffa-notte" />
            </div>
          </div>

          {/* Note */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">Note (opzionale)</label>
            <textarea rows={2} className="w-full border border-zinc-300 rounded-lg p-2 text-sm" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="note" placeholder="es. Pagamento in contanti al check-in" />
          </div>

          {/* Luogo e data */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">Luogo e data</label>
            <Input placeholder="es. Milano, 15/03/2026" value={form.luogo_data} onChange={(e) => setForm({ ...form, luogo_data: e.target.value })} data-testid="luogo-data" />
          </div>

          {/* Firma */}
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-zinc-500 mb-2">Firma locatore</p>
            {savedSignature && !signatureFromSaved && (
              <button type="button" onClick={useSavedSignature} className="text-xs text-teal-700 font-semibold hover:underline mb-2" data-testid="use-saved-sig-btn">
                <Check size={12} className="inline" /> Usa firma salvata
              </button>
            )}
            {signatureFromSaved ? (
              <div className="border border-teal-300 rounded-xl p-3 bg-teal-50" data-testid="signature-saved-preview">
                <img src={savedSignature} alt="Firma" className="h-20 mx-auto" />
                <button type="button" onClick={clearSignature} className="text-xs text-red-600 mt-2">Rifai firma</button>
              </div>
            ) : (
              <div className="border border-zinc-300 rounded-xl overflow-hidden">
                <SignaturePad ref={sigPadRef} data-testid="signature-pad" />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => setView('list')}>Annulla</Button>
            <Button type="submit" disabled={saving} className="bg-teal-700 hover:bg-teal-800 text-white font-bold rounded-xl px-6" data-testid="save-btn">
              {saving ? 'Salvataggio…' : 'Salva e Scarica PDF'}
            </Button>
          </div>
        </motion.form>
      ) : receipts.length === 0 ? (
        <div className="warm-card p-10 text-center" data-testid="empty-state">
          <Warning size={32} className="text-amber-500 mx-auto mb-3" />
          <p className="text-zinc-500">Nessuna ricevuta imposta di soggiorno creata.</p>
          <Button onClick={() => setView('create')} className="mt-4 bg-teal-700 hover:bg-teal-800 rounded-xl" data-testid="empty-create-btn">
            <Plus size={16} className="mr-1.5" /> Crea la prima
          </Button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="receipts-list">
          {receipts.map((r) => (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="warm-card p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 warm-card-interactive"
              data-testid={`receipt-item-${r.id}`}
            >
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-11 h-11 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Coins size={20} weight="fill" className="text-teal-700" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-bold text-zinc-900 truncate">{r.ospite_nome}</p>
                    {r.receipt_number && <span className="text-[10px] font-black uppercase tracking-widest text-zinc-400">N° {r.receipt_number}</span>}
                  </div>
                  <p className="text-xs text-zinc-500 truncate">
                    {r.dal} → {r.al} · {r.num_notti} notti · {r.num_ospiti} ospiti {r.comune && `· ${r.comune}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <p className="text-xl font-black text-teal-700 tabular-nums whitespace-nowrap">€ {r.totale_imposta}</p>
                <Button size="sm" variant="outline" onClick={() => downloadPdf(r)} className="rounded-lg" data-testid={`pdf-btn-${r.id}`}>
                  <FilePdf size={14} className="mr-1" /> PDF
                </Button>
                <button onClick={() => handleDelete(r.id)} className="p-2 text-zinc-400 hover:text-red-600 rounded-lg hover:bg-red-50" data-testid={`delete-btn-${r.id}`} aria-label="Elimina">
                  <Trash size={16} />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
